import { rawIfc } from './rawIfc'
import { decodePlacement, accumulateChain } from './ifcRawParse'
import type { GeoPlacement } from './geoTransform'

// ---------------------------------------------------------------------------
// Cadeia de referenciais EXATA de um elemento, lida do próprio STEP.
//
// Responde a "este elemento está referenciado a quê?": cada
// IfcLocalPlacement da cadeia (elemento → storey → building → site →
// origem do ficheiro) com a origem relativa ao pai e a origem absoluta
// no referencial do ficheiro, ambas em METROS (IfcUnitAssignment).
//
// Convenção: coordenadas do FICHEIRO IFC (x=E, y=N, z=h). A projeção
// para o CRS do mapa é feita por mapPointFromFile com o GeoPlacement
// ativo (MapConversion ou override).
// ---------------------------------------------------------------------------

export interface ElementChainRow {
  /** expressID do IfcLocalPlacement deste nível (null se indisponível). */
  placementID: number | null
  /** Origem relativa ao placement pai, em metros; null sem Location. */
  relative: [number, number, number] | null
  /** Rotação em torno de Z declarada no RefDirection, em graus. */
  rotationDeg: number | null
  /** Eixo Z (Axis) declarado, ratios crus; null quando usa o default
   *  [0,0,1]. Adimensional (direção), não sofre conversão de unidade. */
  axisZ: [number, number, number] | null
  /** Eixo X (RefDirection) declarado, ratios crus; null quando usa o
   *  default [1,0,0]. Adimensional. */
  axisX: [number, number, number] | null
  /** Origem ABSOLUTA no referencial do ficheiro, em metros (E, N, h). */
  absolute: [number, number, number]
}

export interface ElementChainResult {
  unitLabel: string
  factorToMetres: number
  /** Ordem folha→raiz: rows[0] = placement do próprio elemento. */
  rows: ElementChainRow[]
}

/** True quando o parser paralelo já tem este modelo carregado. */
export function elementChainReady(modelId: string): boolean {
  return rawIfc.hasModel(modelId)
}

/** Extrai a cadeia exata de placements do elemento. Carrega o parser
 *  paralelo on-demand (bytes em cache desde o load); em modelos
 *  grandes a primeira chamada custa segundos e memória. */
export async function extractElementChain(
  modelId: string,
  expressID: number,
): Promise<ElementChainResult | null> {
  if (!rawIfc.hasModel(modelId)) {
    await rawIfc.loadModel(modelId)
  }
  if (!rawIfc.hasModel(modelId)) return null
  const unit = rawIfc.getLengthUnit(modelId)
  const factor = unit?.factorToMetres ?? 1
  let chainRaw: unknown[]
  try {
    chainRaw = rawIfc.getPlacementChain(modelId, expressID)
  } catch {
    return null
  }
  const chain = chainRaw
    .map((p) => decodePlacement(p as Parameters<typeof decodePlacement>[0]))
    .filter((p): p is NonNullable<typeof p> => p !== null)
  if (chain.length === 0) return null
  const acc = accumulateChain(chain)
  const asVec3 = (a: number[] | null): [number, number, number] | null =>
    a && a.length >= 3 ? [a[0], a[1], a[2]] : null
  const rows: ElementChainRow[] = chain.map((p, i) => ({
    placementID: p.expressID,
    relative: p.location
      ? [
          p.location[0] * factor,
          p.location[1] * factor,
          p.location[2] * factor,
        ]
      : null,
    rotationDeg: p.rotationDeg,
    axisZ: asVec3(p.axisZ),
    axisX: asVec3(p.axisX),
    absolute: [acc[i].x * factor, acc[i].y * factor, acc[i].z * factor],
  }))
  return {
    unitLabel: unit?.label ?? '(unidade desconhecida)',
    factorToMetres: factor,
    rows,
  }
}

/** Projeta um ponto em coordenadas do FICHEIRO (metros, x=E y=N z=h)
 *  para o CRS do mapa com o GeoPlacement ativo:
 *  E = E0 + s·(x·cosθ − y·sinθ), N = N0 + s·(x·sinθ + y·cosθ),
 *  h = h0 + s·z. */
export function mapPointFromFile(
  placement: GeoPlacement,
  p: [number, number, number],
): [number, number, number] {
  const th = (placement.rotationDeg * Math.PI) / 180
  const s = placement.scale || 1
  const c = Math.cos(th)
  const si = Math.sin(th)
  return [
    placement.eastings + s * (p[0] * c - p[1] * si),
    placement.northings + s * (p[0] * si + p[1] * c),
    placement.orthogonalHeight + s * p[2],
  ]
}
